import IntegrationStatus from './components/IntegrationStatus.jsx'
import BugReporter from './components/BugReporter.jsx'
import AppTooltips from './components/AppTooltips.jsx'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation } from 'react-router-dom'

// Dev/Firebase auth/session selector
import { getSession, signOut } from './lib/auth'
import './firebase'

// Theme
import { initTheme, watchThemeChanges } from './lib/theme'

// Auth
import SignIn from './features/auth/SignIn.jsx'
import RequestPasswordReset from './features/auth/RequestPasswordReset.jsx'
import ResetPassword from './features/auth/ResetPassword.jsx'

// Admin
import AdminHome from './pages/AdminHome.jsx'
import UsersAdminDev from './features/admin/UsersAdminDev.jsx'
import CommunitiesAdminDev from './features/admin/CommunitiesAdminDev.jsx'
import GroupsAdminDev from './features/admin/GroupsAdminDev.jsx'
import SystemConfigDev from './features/admin/SystemConfigDev.jsx'

// Tasks
import MyTasks from './features/tasks/MyTasks.jsx'

// CAF
import CafListDev from './features/caf/CafListDev.jsx'
import CafNew from './features/caf/CafNew.jsx'
import CafDetailDev from './features/caf/CafDetailDev.jsx'

// PM
import PmList from './features/pm/PmList.jsx'
import PmDetail from './features/pm/PmDetail.jsx'
// import PmNew from './features/pm/PmNew.jsx'

// Dashboard
import Dashboard from './features/dashboard/Dashboard.jsx'

// Community
import CommunityDocsDev from './features/communities/CommunityDocsDev.jsx'

// Profile
import UserProfile from './features/profile/UserProfile.jsx'

// Help (NEW — imported instead of inline component)
import Help from './pages/Help.jsx'

// Access scope provider
import { ScopeProvider } from './features/auth/ScopeProvider.jsx'

// Styles
import './app.css'
import ReleaseHistory from './features/releases/ReleaseHistory'
import AppSidebar, { NavIcon } from './components/AppSidebar'

// Production rollout requires an explicit build flag; defaults remain disabled.
const complianceEnabled = import.meta.env.VITE_COMPLIANCE_ENABLED !== 'false'
const Compliance = complianceEnabled
  ? React.lazy(() => import('./features/compliance/WorkflowCompliance.jsx'))
  : null

const CompliancePortfolio = complianceEnabled ? React.lazy(() => import('./features/compliance/CompliancePortfolio.jsx')) : null

// Theme setup
initTheme()
watchThemeChanges()
try { initEmulatorsIfNeeded?.() } catch {}

/* ---------- Helpers ---------- */
function OriginalComplianceRedirect() {
  const { search } = useLocation()
  return <Navigate to={`/compliance${search}`} replace />
}

function useSession(){
  const [s, setS] = React.useState(getSession())
  React.useEffect(() => {
    const refresh = () => setS(getSession())
    window.addEventListener('storage', refresh)
    window.addEventListener('focus', refresh)
    window.addEventListener('sessionchange', refresh)
    refresh()
    return () => {
      window.removeEventListener('storage', refresh)
      window.removeEventListener('focus', refresh)
      window.removeEventListener('sessionchange', refresh)
    }
  }, [])
  return s
}

function PrivateRoute({ children }) {
  const session = useSession()
  if (!session) return <Navigate to="/signin" replace />
  return children
}

function isAdmin(sess){
  const lvl = (sess?.adminLevel || '').toString().toLowerCase()
  return lvl === 'admin' || lvl === 'sysconfig'
}

function AdminRoute({ children }) {
  const session = useSession()
  if (!session) return <Navigate to="/signin" replace />
  if (!isAdmin(session)) {
    return (
      <div className="card">
        <h3>Not Authorized</h3>
        <p>You need <code>admin</code> or <code>sysconfig</code> permissions to view this page.</p>
      </div>
    )
  }
  return children
}

function isSysconfig(sess){
  const lvl = (sess?.adminLevel || '').toString().toLowerCase()
  if (lvl === 'sysconfig') return true
  const roles = Array.isArray(sess?.roles) ? sess.roles.map(r => (r||'').toString().toLowerCase()) : []
  return roles.includes('sysconfig')
}

function SysconfigRoute({ children }) {
  const session = useSession()
  if (!session) return <Navigate to="/signin" replace />
  if (!(isSysconfig(session) || isAdmin(session))) {
    return (
      <div className="card">
        <h3>Not Authorized</h3>
        <p>You need <code>admin</code> or <code>sysconfig</code> permissions to view this page.</p>
      </div>
    )
  }
  return children
}

/* ---------- Header ---------- */
function Header({onOpenNavigation,mobileOpen}){
  const sess = useSession()
  const nav = useNavigate()
  const who = sess ? (sess.displayName || sess.email) : null

  return (
    <header className="app-topbar" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
      <div style={{display:'flex', alignItems:'center', gap:8}}>
        <button className="app-mobile-navigation" aria-label="Open navigation" aria-controls="app-sidebar" aria-expanded={mobileOpen} onClick={onOpenNavigation}><NavIcon name="menu"/></button><strong>MaintainIQ</strong>
      </div>



      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
        {who && (
          <Link
            to="/profile"
            className="pill pill-user"
            data-tooltip={sess?.email || who}
            aria-label="Open profile"
          >
            {who}
          </Link>
        )}

        {sess && <IntegrationStatus key={sess.email} />}
        {sess && <BugReporter />}


        {sess ? (
          <button onClick={async () => { await signOut(); nav('/signin', { replace:true }) }}>
            Sign Out
          </button>
        ) : null}
      </div>
    </header>
  )
}

/* ---------- Shell & Routes ---------- */
function Shell() {
  const session = useSession()
  const [mobileOpen,setMobileOpen]=React.useState(false)
  const closeNavigation=React.useCallback(()=>setMobileOpen(false),[])

  React.useEffect(() => {
    if (!session) {
      const prevOverflow = document.body.style.overflow
      const prevHeight = document.body.style.height
      document.documentElement.style.height = '100%'
      document.body.style.height = '100%'
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = prevOverflow
        document.body.style.height = prevHeight
        document.documentElement.style.height = ''
      }
    }
  }, [session])

  return (
    <div className={session ? 'shell-auth' : 'shell-guest'}>
      {session ? (
        <>
          <Header mobileOpen={mobileOpen} onOpenNavigation={()=>setMobileOpen(true)}/>
          <div className="shell-auth-body">
          <AppSidebar mobileOpen={mobileOpen} onClose={closeNavigation} privileged={isAdmin(session)||isSysconfig(session)} complianceEnabled={complianceEnabled}/>
          <main className="shell-auth-main">
            <div className="container">
              <Routes>
                <Route
                  path="/"
                  element={<Navigate to={session ? "/tasks" : "/signin"} replace />}
                />

                {/* Auth */}
                <Route
                  path="/signin"
                  element={ session ? <Navigate to="/tasks" replace /> : <SignIn /> }
                />
                <Route path="/auth/reset-password" element={<ResetPassword />} />
                <Route path="/auth/request-reset" element={<PrivateRoute><RequestPasswordReset/></PrivateRoute>} />

                {/* Tasks */}
                <Route path="/tasks" element={<PrivateRoute><MyTasks/></PrivateRoute>} />
                {complianceEnabled && <Route path="/compliance" element={<PrivateRoute><React.Suspense fallback={<div className="card">Loading compliance…</div>}><Compliance /></React.Suspense></PrivateRoute>} />}
                {complianceEnabled && <Route path="/compliance/portfolio" element={<PrivateRoute><React.Suspense fallback={<div className="card">Loading portfolio…</div>}><CompliancePortfolio /></React.Suspense></PrivateRoute>} />}
                {complianceEnabled && <Route path="/compliance/rental-license-renewal" element={<PrivateRoute><OriginalComplianceRedirect /></PrivateRoute>} />}
                {complianceEnabled && <Route path="/compliance/operations" element={<PrivateRoute><OriginalComplianceRedirect /></PrivateRoute>} />}

                {/* Dashboard */}
                <Route path="/dashboard" element={<PrivateRoute><Dashboard/></PrivateRoute>} />

                {/* Admin hub */}
                <Route path="/admin" element={<AdminRoute><AdminHome/></AdminRoute>} />
                {complianceEnabled && <><Route path="/compliance/settings" element={<PrivateRoute><React.Suspense fallback={<div className="card">Loading compliance settings…</div>}><Compliance key="compliance-settings" adminView /></React.Suspense></PrivateRoute>} /><Route path="/admin/compliance" element={<Navigate to="/compliance/settings" replace />} /></>}
                <Route path="/admin/users" element={<AdminRoute><UsersAdminDev/></AdminRoute>} />
                <Route path="/admin/communities" element={<AdminRoute><CommunitiesAdminDev/></AdminRoute>} />
                <Route path="/admin/groups" element={<AdminRoute><GroupsAdminDev/></AdminRoute>} />
                <Route path="/admin/system" element={<SysconfigRoute><SystemConfigDev/></SysconfigRoute>} />

                {/* CAF */}
                <Route path="/dev/caf" element={<PrivateRoute><CafListDev/></PrivateRoute>} />
                <Route path="/dev/caf/new" element={<PrivateRoute><CafNew/></PrivateRoute>} />
                <Route path="/dev/caf/:id" element={<PrivateRoute><CafDetailDev/></PrivateRoute>} />

                {/* PM */}
                <Route path="/pm" element={<PmList />} />
                <Route path="/pm/new" element={<PmDetail />} />
                <Route path="/pm/:pmId" element={<PmDetail />} />

                {/* Community */}
                <Route path="/docs" element={<PrivateRoute><CommunityDocsDev/></PrivateRoute>} />

                {/* Profile */}
                <Route path="/profile" element={<PrivateRoute><UserProfile /></PrivateRoute>} />

                {/* Help — now imported */}
                <Route path="/help" element={<Help />} />
                <Route path="/releases" element={<PrivateRoute><ReleaseHistory /></PrivateRoute>} />

                {/* 404 */}
                <Route path="*" element={
                  <div className="card">
                    <h3>Not Found</h3>
                    <p>Check the URL or use the navigation.</p>
                  </div>
                } />
              </Routes>
            </div>
          </main>
          </div>
        </>
      ) : (
        <div className="container">
          <Routes>
          <Route
            path="/"
            element={<Navigate to={session ? "/tasks" : "/signin"} replace />}
          />

          {/* Auth */}
          <Route
            path="/signin"
            element={ session ? <Navigate to="/tasks" replace /> : <SignIn /> }
          />
          <Route path="/auth/reset-password" element={<ResetPassword />} />
          <Route path="/auth/request-reset" element={<PrivateRoute><RequestPasswordReset/></PrivateRoute>} />

          {/* Tasks */}
          <Route path="/tasks" element={<PrivateRoute><MyTasks/></PrivateRoute>} />
                {complianceEnabled && <Route path="/compliance" element={<PrivateRoute><React.Suspense fallback={<div className="card">Loading compliance…</div>}><Compliance /></React.Suspense></PrivateRoute>} />}
                {complianceEnabled && <Route path="/compliance/portfolio" element={<PrivateRoute><React.Suspense fallback={<div className="card">Loading portfolio…</div>}><CompliancePortfolio /></React.Suspense></PrivateRoute>} />}
                {complianceEnabled && <Route path="/compliance/rental-license-renewal" element={<PrivateRoute><OriginalComplianceRedirect /></PrivateRoute>} />}
                {complianceEnabled && <Route path="/compliance/operations" element={<PrivateRoute><OriginalComplianceRedirect /></PrivateRoute>} />}

          {/* Dashboard */}
          <Route path="/dashboard" element={<PrivateRoute><Dashboard/></PrivateRoute>} />

          {/* Admin hub */}
          <Route path="/admin" element={<AdminRoute><AdminHome/></AdminRoute>} />
                {complianceEnabled && <><Route path="/compliance/settings" element={<PrivateRoute><React.Suspense fallback={<div className="card">Loading compliance settings…</div>}><Compliance key="compliance-settings" adminView /></React.Suspense></PrivateRoute>} /><Route path="/admin/compliance" element={<Navigate to="/compliance/settings" replace />} /></>}
          <Route path="/admin/users" element={<AdminRoute><UsersAdminDev/></AdminRoute>} />
          <Route path="/admin/communities" element={<AdminRoute><CommunitiesAdminDev/></AdminRoute>} />
          <Route path="/admin/groups" element={<AdminRoute><GroupsAdminDev/></AdminRoute>} />
          <Route path="/admin/system" element={<SysconfigRoute><SystemConfigDev/></SysconfigRoute>} />

          {/* CAF */}
          <Route path="/dev/caf" element={<PrivateRoute><CafListDev/></PrivateRoute>} />
          <Route path="/dev/caf/new" element={<PrivateRoute><CafNew/></PrivateRoute>} />
          <Route path="/dev/caf/:id" element={<PrivateRoute><CafDetailDev/></PrivateRoute>} />

          {/* PM */}
          <Route path="/pm" element={<PmList />} />
          <Route path="/pm/new" element={<PmDetail />} />
          <Route path="/pm/:pmId" element={<PmDetail />} />

          {/* Community */}
          <Route path="/docs" element={<PrivateRoute><CommunityDocsDev/></PrivateRoute>} />

          {/* Profile */}
          <Route path="/profile" element={<PrivateRoute><UserProfile /></PrivateRoute>} />

          {/* Help — now imported */}
          <Route path="/help" element={<Help />} />
                <Route path="/releases" element={<PrivateRoute><ReleaseHistory /></PrivateRoute>} />

          {/* 404 */}
          <Route path="*" element={
            <div className="card">
              <h3>Not Found</h3>
              <p>Check the URL or use the navigation.</p>
            </div>
          } />
          </Routes>
        </div>
      )}

      <style>{`
        .shell-guest {
          min-height: 100svh;
          background: var(--color-bg);
          overflow: hidden;
        }
        .shell-guest .container {
          height: 100svh;
          display: grid;
          place-items: center;
          padding: 0;
          margin: 0;
        }
      `}</style>
    </div>
  )
}

/* ---------- Render ---------- */
createRoot(document.getElementById('root')).render(
  <ScopeProvider>
    <BrowserRouter>
      <Shell />
      <AppTooltips />
    </BrowserRouter>
  </ScopeProvider>
)
